// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { ActionIcon, Alert, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconUserMinus } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { POLARIS_PROVIDER_ROLE_SYSTEM } from '../../constants/appointmentTypes';
import { botIdentifier } from '../../lib/bot-identifier';
import { showErrorNotification } from '../../utils/notifications';
import { useSweList } from './hooks/useSweList';
import { extractPatientIdsFromMembership, resolvePatientAccessPolicyRef } from './utils/swePatientAccess';

interface GrantRow {
  id: string;
  sweId: string;
  patientId: string;
  sweName: string;
  patientName: string;
  mrn: string;
}

export function GrantListTab(): JSX.Element {
  const medplum = useMedplum();
  const { rows: sweRows, error: sweError, loading: sweListLoading } = useSweList();
  const [allGrants, setAllGrants] = useState<GrantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingIds, setRevokingIds] = useState<Set<string>>(new Set());

  const loadAllGrants = useCallback(
    async (noCache = false) => {
      const searchOptions = noCache ? { cache: 'no-cache' as const } : undefined;
      try {
        setLoading(true);

        // Filter server-side by the SWE role token — fetching the first 500 and
        // filtering client-side drops freshly-created SWE roles once the project
        // holds >500 PractitionerRole resources. `role` maps to PractitionerRole.code.
        const roles = await medplum.searchResources(
          'PractitionerRole',
          { role: `${POLARIS_PROVIDER_ROLE_SYSTEM}|software-engineer`, _count: '500' },
          searchOptions
        );

        const sweMap = new Map<string, string>();
        for (const role of roles) {
          const ref = role.practitioner?.reference;
          if (ref) {
            const id = ref.split('/')[1];
            if (id) {
              try {
                const p = await medplum.readResource('Practitioner', id);
                const name = [p.name?.[0]?.given?.join(' '), p.name?.[0]?.family].filter(Boolean).join(' ');
                sweMap.set(id, name || 'Unknown');
              } catch {
                // skip deleted practitioners
              }
            }
          }
        }

        const patientPolicyRef = await resolvePatientAccessPolicyRef(medplum);
        const swePatientIds = new Map<string, string[]>();

        for (const [sweId] of sweMap) {
          const membership = await medplum.searchOne(
            'ProjectMembership',
            { profile: `Practitioner/${sweId}` },
            searchOptions
          );
          const patientIds = extractPatientIdsFromMembership(membership, patientPolicyRef);
          if (patientIds.length > 0) {
            swePatientIds.set(sweId, patientIds);
          }
        }

        const uniquePatientIds = [...new Set([...swePatientIds.values()].flat())];
        const patientById = new Map<string, Patient>();

        if (uniquePatientIds.length > 0) {
          const patients = await medplum.searchResources(
            'Patient',
            { _id: uniquePatientIds.join(','), _count: '500' },
            searchOptions
          );

          for (const patient of patients) {
            if (patient.id) {
              patientById.set(patient.id, patient);
            }
          }
        }

        const grants: GrantRow[] = [];
        for (const [sweId, sweName] of sweMap) {
          const patientIds = swePatientIds.get(sweId) ?? [];

          for (const patientId of patientIds) {
            const patient = patientById.get(patientId);
            if (!patient) {
              continue;
            }

            const patientName = [patient.name?.[0]?.given?.join(' '), patient.name?.[0]?.family]
              .filter(Boolean)
              .join(' ');
            const mrn = patient.identifier?.find((id) => id.system === 'http://polarissleep.com/mrn')?.value ?? '';

            grants.push({
              id: `${sweId}-${patientId}`,
              sweId,
              patientId,
              sweName,
              patientName: patientName || 'Unknown',
              mrn,
            });
          }
        }

        setAllGrants(grants);
      } catch (err) {
        console.error('[GrantListTab] Failed to load grants:', err);
      } finally {
        setLoading(false);
      }
    },
    [medplum]
  );

  useEffect(() => {
    loadAllGrants(true).catch(console.error);
  }, [loadAllGrants]);

  const handleRevoke = useCallback(
    async (row: GrantRow) => {
      setRevokingIds((prev) => new Set(prev).add(row.id));
      try {
        await medplum.executeBot(botIdentifier('revoke-swe-patient-access'), {
          sweId: row.sweId,
          patientIds: [row.patientId],
        });
        notifications.show({
          color: 'green',
          title: 'Access revoked',
          message: `Removed ${row.patientName} from ${row.sweName}'s grants.`,
        });
        await loadAllGrants(true);
      } catch (err) {
        showErrorNotification(err);
      } finally {
        setRevokingIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }
    },
    [medplum, loadAllGrants]
  );

  return (
    <Stack gap="md">
      {sweError && (
        <Alert color="red" title="Could not load grants">
          {sweError}
        </Alert>
      )}

      <Paper withBorder p="lg" radius="md" shadow="sm">
        <Stack gap="md">
          <Text fw={500} size="sm">
            All SWE Grants ({allGrants.length} total)
          </Text>
          {sweRows.length === 0 && !sweListLoading ? (
            <Text c="dimmed" size="sm">
              No Software Engineers found
            </Text>
          ) : (
            <DataTable
              fetching={loading || sweListLoading}
              minHeight={200}
              records={allGrants}
              idAccessor={(row) => row.id}
              striped
              highlightOnHover
              fz="sm"
              noRecordsText={allGrants.length === 0 && !loading ? 'No grants' : 'Loading...'}
              columns={[
                { accessor: 'sweName', title: 'Software Engineer' },
                { accessor: 'patientName', title: 'Patient Name' },
                {
                  accessor: 'mrn',
                  title: 'MRN',
                  render: (row: GrantRow) => row.mrn || '—',
                },
                {
                  accessor: 'actions',
                  title: '',
                  textAlign: 'right',
                  render: (row: GrantRow) => (
                    <Tooltip label="Revoke access" withArrow>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        size="sm"
                        loading={revokingIds.has(row.id)}
                        onClick={() => handleRevoke(row).catch(console.error)}
                        data-testid={`revoke-grant-${row.id}`}
                        aria-label={`Revoke ${row.patientName} from ${row.sweName}`}
                      >
                        <IconUserMinus size={14} />
                      </ActionIcon>
                    </Tooltip>
                  ),
                },
              ]}
            />
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
